import React from 'react';
import { MaterialReactTable } from 'material-react-table';
export default function TableDisplay({
                                         columns = [],
                                         data = [],
                                         title = '',
                                         pageSizeOptions = [5, 10, 20],
                                         options = {},
                                         rowStyle,
                                         style,
                                     }) {
    // Detect if this is a narrow-column table (like team page) vs regular table
    const hasNarrowColumns = React.useMemo(() => {
        return columns.some(col => {
            const field = col.field?.toLowerCase() || '';
            const title = col.title?.toLowerCase() || '';
            return field.includes('online') || title.includes('online') ||
                   field.includes('status') || title.includes('status');
        });
    }, [columns]);

    // Detect if this is a personnel page (needs tighter spacing)
    const isPersonnelPage = React.useMemo(() => {
        return columns.some(col => {
            const field = col.field?.toLowerCase() || '';
            const title = col.title?.toLowerCase() || '';
            return title.includes('personnel name') || field.includes('personnel');
        });
    }, [columns]);

    const mrtColumns = React.useMemo(
        () =>
            columns.map((col, idx) => {
                const { field, title: header, render, size: providedSize, minSize: providedMinSize, maxSize: providedMaxSize, ...rest } = col;

                const accessorKey = field || `col_${idx}`;

                // For custom render functions
                const Cell = typeof render === 'function'
                    ? ({ row }) => render(row.original)
                    : undefined;

                // Calculate field/header strings for all logic
                const fieldLower = field?.toLowerCase() ?? '';
                const headerLower = header?.toLowerCase() ?? '';
                const combinedField = `${fieldLower} ${headerLower}`;

                // If size is provided directly in column definition, use it
                let columnSize = providedSize;
                let columnMinSize = providedMinSize || 40;
                let columnMaxSize = providedMaxSize || 800;

                // Otherwise, calculate based on field/header names
                if (!columnSize) {
                    // Column width logic - wider default for regular tables, narrower for tables with many narrow columns
                    columnSize = hasNarrowColumns ? 120 : 160; // default

                    // Actions column needs more space for buttons
                    if (combinedField.includes('action')) {
                        columnSize = hasNarrowColumns ? 280 : 220; // 280 for team page, 220 for others
                    }
                    // Compact columns
                    else if (['firstname', 'first name'].some(f => combinedField.includes(f))) {
                        columnSize = 110;
                    } else if (['lastname', 'last name'].some(f => combinedField.includes(f))) {
                        columnSize = 110;
                    } else if (combinedField.includes('online')) {
                        columnSize = 78;
                    } else if (combinedField.includes('status')) {
                        columnSize = 80;
                    } else if (combinedField.includes('email')) {
                        columnSize = hasNarrowColumns ? 220 : (isPersonnelPage ? 160 : 200);
                    } else if (combinedField.includes('phone') || combinedField.includes('mobile')) {
                        columnSize = 130;
                    } else if (combinedField.includes('role')) {
                        columnSize = 100;
                    } else if (combinedField.includes('department')) {
                        columnSize = 100;
                    } else if (combinedField.includes('token') || combinedField.includes('link')) {
                        columnSize = 400;
                    } else if (combinedField.includes('personnel name')) {
                        columnSize = 180;
                    } else if (combinedField.includes('connector id')) {
                        columnSize = 120;
                    } else if (combinedField.includes('type') && !combinedField.includes('action')) {
                        columnSize = 100;
                    } else if (combinedField.includes('qr code')) {
                        columnSize = 100;
                    } else if (combinedField.includes('name')) {
                        columnSize = hasNarrowColumns ? 110 : 240;
                    } else if (combinedField.includes('frequency')) {
                        columnSize = 200;
                    } else if (combinedField.includes('last send') || combinedField.includes('lastsend')) {
                        columnSize = 220;
                    } else if (combinedField.includes('receiver')) {
                        columnSize = 240;
                    }
                }

                // Enable word wrap for selected fields
                const enableWrap = ['email','name', 'link', 'token', 'first name', 'lastname', 'last name'].some(f =>
                    fieldLower.includes(f)
                );

                const columnDef = {
                    accessorKey,
                    header: header || field || `Column ${idx}`,
                    size: columnSize,
                    minSize: columnMinSize,
                    maxSize: columnMaxSize,
                    muiTableBodyCellProps: {
                        sx: {
                            whiteSpace: enableWrap ? 'normal' : 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            wordBreak: enableWrap ? 'break-word' : 'normal',
                            paddingLeft: options?.uniformColumnWidths ? '6px' : (idx === 0 && !hasNarrowColumns && !isPersonnelPage ? '24px' : (hasNarrowColumns ? '6px' : (isPersonnelPage ? '8px' : '14px'))),
                            paddingRight: options?.uniformColumnWidths ? '6px' : (hasNarrowColumns ? '6px' : (isPersonnelPage ? '8px' : '14px')),
                            fontSize: '0.875rem',
                        },
                    },
                    muiTableHeadCellProps: {
                        sx: {
                            paddingLeft: options?.uniformColumnWidths ? '6px' : (idx === 0 && !hasNarrowColumns && !isPersonnelPage ? '24px' : (hasNarrowColumns ? '6px' : (isPersonnelPage ? '8px' : '14px'))),
                            paddingRight: options?.uniformColumnWidths ? '6px' : (hasNarrowColumns ? '6px' : (isPersonnelPage ? '8px' : '14px')),
                            fontWeight: 'bold',
                            fontSize: '0.85rem',
                        },
                    },
                    ...rest,
                };

                // Add Cell only if it exists
                if (Cell) {
                    columnDef.Cell = Cell;
                }

                return columnDef;
            }),
        [columns]
    );

    const initialState = {
        pagination: { pageSize: pageSizeOptions?.[0] ?? 5, pageIndex: 0 },
        showGlobalFilter: true,
    };

    const tableOptions = {
        enableSorting: options?.sorting ?? true,
        enableColumnOrdering: options?.draggable ?? false,
        enableMultiSort: options?.thirdSortClick ?? false,
        enableGlobalFilter: true,
        enableColumnFilters: false,
        initialState,
        // Minimal responsive features - no column menus or fullscreen yet
        enableColumnResizing: false, // Disabled for now
        enableHiding: false, // Disabled - no column menu
        enableColumnPinning: false, // Disabled for now
        enableDensityToggle: false,
        enableFullScreenToggle: false, // Disabled - user doesn't need yet
        enableColumnActions: false, // Disabled - no 3-dot menu in headers
        // Keep toolbar actions hidden
        renderToolbarInternalActions: () => null,
        // Layout mode for better responsiveness
        layoutMode: 'semantic', // Use semantic layout for better column distribution
        // Responsive container with horizontal scroll
        muiTableContainerProps: {
            sx: {
                maxWidth: '100%',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
            },
        },
        // Table props - use fixed layout for narrow-column tables (team), auto for others
        muiTableProps: {
            sx: {
                tableLayout: options?.uniformColumnWidths ? 'fixed' : (hasNarrowColumns ? 'fixed' : 'auto'),
                width: '100%',
            },
        },
        muiTableBodyRowProps: rowStyle
            ? ({ row }) => ({
                sx: {
                    ...rowStyle(row.original),
                    '& > td': { paddingY: '10px' },
                },
            })
            : {
                sx: { '& > td': { paddingY: '10px' } },
            },
    };

    const EmptyRowsFallback = () => (
        <tr style={{ height: 180 }}>
            <td colSpan={columns.length} style={{ padding: '48px 30px', textAlign: 'center', verticalAlign: 'middle' }}>
                No records to display
            </td>
        </tr>
    );

    return (
        <div style={{ padding: options?.topPaddingOnly ? '16px 0 0 0' : '0', ...style }}>
            {title && (
                <h2
                    style={{
                        fontSize: '1.2rem',
                        fontFamily: '"Inter","Roboto","Helvetica Neue","Helvetica","Arial",sans-serif',
                        fontWeight: 510,
                        lineHeight: 1.5,
                        letterSpacing: '0.007em',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        marginBottom: '0.5rem',
                    }}
                >
                    {title}
                </h2>
            )}

            <MaterialReactTable
                columns={mrtColumns}
                data={data}
                {...tableOptions}
                renderEmptyRowsFallback={EmptyRowsFallback}
            />
        </div>
    );
}